import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { submissionsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
    FileText,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    MessageSquare,
    Send,
    Loader2,
    ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

const statusConfig = {
    pending: { label: 'Pending Review', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
    needs_info: { label: 'Needs More Info', color: 'bg-orange-100 text-orange-800 border-orange-200', icon: AlertCircle },
    approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
    step1_approved: { label: 'Step 1 Approved', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle },
};

const SubmissionCard = ({ submission, onReplySubmitted }) => {
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const [expanded, setExpanded] = useState(submission.status === 'needs_info');

    const status = statusConfig[submission.status] || statusConfig.pending;
    const StatusIcon = status.icon;
    const replies = submission.replies || [];

    const handleReply = async () => {
        if (!replyText.trim()) return;
        setSending(true);
        try {
            await submissionsAPI.reply(submission.submission_id, replyText.trim());
            toast.success('Reply sent successfully');
            setReplyText('');
            onReplySubmitted();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    return (
        <Card className="overflow-hidden" data-testid={`submission-card-${submission.submission_id}`}>
            <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setExpanded(!expanded)}>
                <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                        <p className="font-semibold truncate">{submission.name}</p>
                        <p className="text-sm text-muted-foreground">{submission.state} {submission.city ? `- ${submission.city}` : ''}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <Badge variant="outline" className={`${status.color} gap-1`}>
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                        {new Date(submission.created_at).toLocaleDateString()}
                    </span>
                </div>
            </div>

            {expanded && (
                <CardContent className="pt-0 border-t">
                    {/* Admin info request */}
                    {submission.info_request_message && (
                        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="info-request-banner">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-semibold text-amber-800 text-sm">Additional Information Requested</p>
                                    <p className="text-sm text-amber-700 mt-1 whitespace-pre-wrap">{submission.info_request_message}</p>
                                    {submission.reviewed_by_name && (
                                        <p className="text-xs text-amber-500 mt-2">— {submission.reviewed_by_name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Conversation thread */}
                    {replies.length > 0 && (
                        <div className="mt-4 space-y-3" data-testid="replies-thread">
                            <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                                <MessageSquare className="h-4 w-4" />
                                Conversation ({replies.length})
                            </p>
                            {replies.map((reply) => (
                                <div
                                    key={reply.reply_id}
                                    className={`rounded-lg p-3 text-sm ${
                                        reply.author_role === 'admin'
                                            ? 'bg-primary/5 border border-primary/20 ml-0 mr-8'
                                            : 'bg-muted ml-8 mr-0'
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="font-medium text-xs">
                                            {reply.author_role === 'admin' ? `${reply.author_name} (Admin)` : reply.author_name}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(reply.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    <p className="whitespace-pre-wrap">{reply.message}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Reply input (only for needs_info or pending) */}
                    {['needs_info', 'pending'].includes(submission.status) && (
                        <div className="mt-4 space-y-3" data-testid="reply-section">
                            <Textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Type your reply here..."
                                rows={3}
                                data-testid="reply-textarea"
                            />
                            <div className="flex justify-end">
                                <Button
                                    onClick={handleReply}
                                    disabled={!replyText.trim() || sending}
                                    size="sm"
                                    className="gap-2"
                                    data-testid="send-reply-btn"
                                >
                                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                    Send Reply
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Submission details */}
                    <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                        {submission.mission && (
                            <div className="sm:col-span-2">
                                <span className="text-muted-foreground">Mission:</span>
                                <p className="mt-0.5">{submission.mission}</p>
                            </div>
                        )}
                        {submission.website && (
                            <div>
                                <span className="text-muted-foreground">Website:</span>
                                <p className="mt-0.5"><a href={submission.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">{submission.website}</a></p>
                            </div>
                        )}
                        {submission.contact_name && (
                            <div>
                                <span className="text-muted-foreground">Contact:</span>
                                <p className="mt-0.5">{submission.contact_name}</p>
                            </div>
                        )}
                    </div>
                </CardContent>
            )}
        </Card>
    );
};

export default function MySubmissionsPage() {
    const { isAuthenticated } = useAuth();
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchSubmissions = async () => {
        try {
            const res = await submissionsAPI.getMine();
            setSubmissions(res.data);
        } catch (error) {
            console.error('Failed to load submissions:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated) fetchSubmissions();
    }, [isAuthenticated]);

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardContent className="pt-6 text-center">
                        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h2 className="text-xl font-semibold mb-2">Sign In Required</h2>
                        <p className="text-muted-foreground">Please log in to view your submissions.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const needsInfoCount = submissions.filter(s => s.status === 'needs_info').length;

    return (
        <div className="min-h-screen bg-muted/30" data-testid="my-submissions-page">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
                        <ArrowLeft className="h-4 w-4" />
                        Back to Home
                    </Link>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <FileText className="h-8 w-8 text-primary" />
                        My Submissions
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Track the status of your organization applications and respond to information requests
                    </p>
                    {needsInfoCount > 0 && (
                        <div className="mt-3 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                            <AlertCircle className="h-4 w-4" />
                            {needsInfoCount} submission{needsInfoCount > 1 ? 's' : ''} need{needsInfoCount === 1 ? 's' : ''} your response
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="space-y-4">
                        {[1, 2].map(i => (
                            <Card key={i}>
                                <CardContent className="pt-6">
                                    <div className="h-6 bg-muted animate-pulse rounded w-1/3 mb-2" />
                                    <div className="h-4 bg-muted animate-pulse rounded w-1/4" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : submissions.length === 0 ? (
                    <Card>
                        <CardContent className="py-16 text-center">
                            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-semibold mb-2">No Submissions Yet</h3>
                            <p className="text-muted-foreground mb-6">
                                You haven't submitted any applications yet. Get started by nominating an organization or applying for membership.
                            </p>
                            <div className="flex gap-3 justify-center">
                                <Link to="/nominate">
                                    <Button variant="outline" size="sm">Nominate a Member</Button>
                                </Link>
                                <Link to="/apply">
                                    <Button size="sm">Apply for Membership</Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {submissions.map((sub) => (
                            <SubmissionCard
                                key={sub.submission_id}
                                submission={sub}
                                onReplySubmitted={fetchSubmissions}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
